import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import { parseCC4FileLocally } from '../../utils/parseCC4FileLocally';

const CC4FileUpload = ({ onUploadSuccess, disabled }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const validExtensions = ['cc4', 'zip'];
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Please select a CC4 or ZIP file');
      return;
    }
    
    // Check if file is too large (show warning but still allow upload)
    if (file.size > 100 * 1024 * 1024) { // 100MB
      toast.warning('File is larger than 100MB. Upload may take some time.');
    }
    
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || disabled) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Show a message for large files
      if (selectedFile.size > 50 * 1024 * 1024) { // 50MB
        toast.info('Large file detected. Processing may take some time. Please be patient.');
      }
      
      // Track processing progress
      const handleProgress = (progress) => {
        setUploadProgress(progress);
      };
      
      // Process the file locally with progress tracking
      const result = await parseCC4FileLocally(selectedFile, handleProgress);
      
      
      // Set to 100% when complete
      setUploadProgress(100);
      
      // Parse the result
      const parsedData = JSON.parse(result.result);
      console.log("CC4 Data", parsedData);
      
      // Extract all VO2 max report data
      const simplifiedData = {
        // VO2 Max data
        vo2Max: parsedData.vo2Max || '',
        vo2MaxUnit: parsedData.vo2MaxUnit || 'ml/kg/min',
        heartRateMax: parsedData.heartRateMax || '',
        heartRateRest: parsedData.heartRateRest || '',
        peakHeartRate: parsedData.peakHeartRate || '',
        fitnessLevel: parsedData.fitnessLevel || '',
        
        // RMR data
        rmrValue: parsedData.rmrValue || parsedData.rmr || '',
        rmrUnit: parsedData.rmrUnit || 'kcal/day',
        
        // Anaerobic threshold data
        anaerobicThreshold: parsedData.anaerobicThreshold || '',
        anaerobicThresholdHR: parsedData.anaerobicThresholdHR || '',
        anaerobicThresholdPercent: parsedData.anaerobicThresholdPercent || '',
        

        aerobicThreshold: parsedData.aerobicThreshold || '',
        aerobicThresholdHR: parsedData.aerobicThresholdHR || '',
        aerobicThresholdPercent: parsedData.aerobicThresholdPercent || '',
        

        // Predicted values
        predictedVO2Max: parsedData.predictedVO2Max || '',
        predictedMaxHR: parsedData.predictedMaxHR || '',
        
        // Recovery data
        recoveryHeartRates: parsedData.recoveryHeartRates || {},
        recoveryVO2Values: parsedData.recoveryVO2Values || {},
        recoveryIndices: parsedData.recoveryIndices || {},
        
        // Test information
        testDate: parsedData.testDate || '',
        testId: parsedData.testId || '',
        reportId: parsedData.reportId || '',
        
        // Heart rate zones
        heartRateZones: parsedData.heartRateZones || {},
        
        // Data samples for graph
        dataSamples: parsedData.dataSamples || [],
        
        // Stage data for dynamic table
        stageData: parsedData.stageData || {},
        
        // Basic patient info
        patientInfo: parsedData.patientInfo || {}
      };
      
      // Notify success
      toast.success('CC4 file processed successfully');
      
      // Pass the simplified data to the parent component
      onUploadSuccess(simplifiedData);
      
      // Reset the file input
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      // Clear any existing toasts
      toast.dismiss();
      
      // Handle error types
      toast.error(`Processing failed: ${error.message || 'Unknown error'}`);
      console.error('CC4 processing error:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mb-6 p-4 border rounded-md bg-gray-50">
      <h3 className="text-lg font-medium mb-2">Upload CC4 File</h3>
      <p className="text-sm text-gray-600 mb-4">
        Upload a CC4 file to automatically extract and fill the form with VO2Max data (processed locally).
      </p>
      
      <div className="flex flex-col sm:flex-row items-start gap-3">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          disabled={isUploading || disabled}
          className="flex-1 text-sm"
          accept=".cc4,.zip"
        />
        
        <div className="flex gap-2">
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading || disabled}
            className="whitespace-nowrap"
          >
            {isUploading ? 'Processing...' : 'Process File'}
          </Button>
          
          {selectedFile && !isUploading && (
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={disabled}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
      
      {selectedFile && !isUploading && (
        <div className="mt-2 text-sm text-gray-700">
          Selected file: {selectedFile.name} ({selectedFile && selectedFile.size ? (selectedFile.size / (1024 * 1024)).toFixed(2) : '0'} MB)
        </div>
      )}
      
      {isUploading && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-1">
            <Spinner size="sm" />
            <span className="text-sm font-medium">
              {uploadProgress < 50 
                ? 'Reading file...' 
                : 'Processing CC4 data...'}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(uploadProgress)}%
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-primary-600 h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          
          <p className="text-xs text-gray-500 mt-1">
            Large files may take some time to process locally.
            {uploadProgress > 0 && uploadProgress < 100 && uploadProgress === Math.round(uploadProgress) && 
              <span className="ml-1 font-medium">
                Please don't refresh the page.
              </span>
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default CC4FileUpload;
